"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "@/contexts/toast-context";
import type {
  DetectedTransaction,
  WatchedWallet,
} from "@/lib/scanner/types";
import { type Trade, settlementEntries, type SettlementEntry } from "@/lib/mock-data";
import { playCoinReceived, playCoinSent } from "@/lib/sounds";

function mergeSettlements(entries: SettlementEntry[]) {
  for (const s of entries) {
    if (!settlementEntries.some(e => e.id === s.id)) {
      settlementEntries.push(s);
    }
  }
}

export function useScanner() {
  const { toast } = useToast();
  const [transactions, setTransactions] = useState<DetectedTransaction[]>([]);
  const [wallets, setWallets] = useState<WatchedWallet[]>([]);
  const [scannerTrades, setScannerTrades] = useState<Trade[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [lastPoll, setLastPoll] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [marketRate, setMarketRate] = useState<{ buy: number | null; sell: number | null }>({ buy: null, sell: null });
  const [lastTradeCreation, setLastTradeCreation] = useState(0);
  const knownTxCountRef = useRef(0);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Load existing data on mount
  useEffect(() => {
    fetch("/api/scanner/transactions")
      .then((r) => r.json())
      .then((data) => {
        if (data.transactions) {
          setTransactions(data.transactions);
          knownTxCountRef.current = data.transactions.length;
        }
        if (data.trades) setScannerTrades(data.trades);
        if (data.settlements) mergeSettlements(data.settlements);
      })
      .catch(() => {});

    fetch("/api/scanner/wallets")
      .then((r) => r.json())
      .then((data) => {
        if (data.wallets) setWallets(data.wallets);
      })
      .catch(() => {});
  }, []);

  // Subscribe to SSE for real-time updates from the background scanner
  useEffect(() => {
    if (!enabled) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    const es = new EventSource("/api/scanner/events");
    eventSourceRef.current = es;
    setIsPolling(true);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "init") {
          setLastPoll(data.lastPoll);
          knownTxCountRef.current = data.transactionCount;
        }

        if (data.type === "update") {
          setLastPoll(data.lastPoll);

          // New transactions detected by the background scanner
          if (data.newTransactions && data.newTransactions.length > 0) {
            setTransactions((prev) => [...data.newTransactions, ...prev]);
            knownTxCountRef.current = data.transactionCount;

            // Play sound based on direction
            const hasIncoming = (data.newTransactions as DetectedTransaction[]).some(
              (tx) => tx.direction === "incoming"
            );
            if (hasIncoming) {
              playCoinReceived();
            } else {
              playCoinSent();
            }

            // Show toast for each new transaction
            for (const tx of data.newTransactions as DetectedTransaction[]) {
              const verb = tx.direction === "incoming" ? "Received" : "Sent";
              const amt = tx.amount.toLocaleString("en-US", {
                maximumFractionDigits: 2,
              });
              const entity = tx.senderEntity ? ` (${tx.senderEntity})` : "";
              toast(
                `${verb} ${amt} USDT on ${tx.chain.toUpperCase()}${entity}`,
                tx.direction === "incoming" ? "success" : "info"
              );
            }

            // Refetch trades to get newly created ones
            fetch("/api/scanner/transactions")
              .then((r) => r.json())
              .then((d) => {
                if (d.trades) setScannerTrades(d.trades);
                if (d.settlements) mergeSettlements(d.settlements);
              })
              .catch(() => {});
            setLastTradeCreation((c) => c + 1);
          }
        }
      } catch {
        // Ignore parse errors (e.g. ping comments)
      }
    };

    es.onerror = () => {
      // SSE will auto-reconnect. Mark as not polling temporarily.
      setIsPolling(false);
      setTimeout(() => {
        if (eventSourceRef.current === es) setIsPolling(true);
      }, 3000);
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
      setIsPolling(false);
    };
  }, [enabled, toast]);

  // Keep poll() for manual trigger / backward compat (now just reloads data)
  const poll = useCallback(async () => {
    try {
      const res = await fetch("/api/scanner/transactions");
      const data = await res.json();
      if (data.transactions) setTransactions(data.transactions);
      if (data.trades) setScannerTrades(data.trades);
      if (data.settlements) mergeSettlements(data.settlements);
    } catch {}
  }, []);

  const confirmMatch = useCallback(
    async (transactionId: string, tradeId: number) => {
      const res = await fetch("/api/scanner/match", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transactionId, tradeId }),
      });
      const data = await res.json();
      if (data.success) {
        setTransactions((prev) =>
          prev.map((t) =>
            t.id === transactionId ? { ...t, matchedTradeId: tradeId } : t
          )
        );
        toast("Transaction matched to trade");
      } else {
        toast(data.error || "Failed to match", "error");
      }
      return data;
    },
    [toast]
  );

  const addWallet = useCallback(
    async (address: string, chain: string, label: string) => {
      const res = await fetch("/api/scanner/wallets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address, chain, label }),
      });
      const data = await res.json();
      if (data.wallet) {
        setWallets((prev) => [...prev, data.wallet]);
        toast("Wallet added");
      } else {
        toast(data.error || "Failed to add wallet", "error");
      }
      return data;
    },
    [toast]
  );

  const deleteWallet = useCallback(
    async (id: string) => {
      await fetch(`/api/scanner/wallets?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      setWallets((prev) => prev.filter((w) => w.id !== id));
      toast("Wallet removed");
    },
    [toast]
  );

  // Update a scanner trade (assign counterparty, edit rate, etc.)
  const updateScannerTrade = useCallback(
    async (tradeId: number, updates: { counterpartyName?: string | null; counterpartyId?: number | null; rate?: number; direction?: string }) => {
      try {
        const res = await fetch("/api/scanner/trades", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tradeId, ...updates }),
        });
        const data = await res.json();
        if (data.success) {
          setScannerTrades((prev) =>
            prev.map((t) => (t.id === tradeId ? data.trade : t))
          );
        } else {
          toast(data.error || "Failed to update trade", "error");
        }
        return data;
      } catch (err) {
        toast((err as Error).message, "error");
        return { success: false };
      }
    },
    [toast]
  );

  // Reload transactions and trades from server
  const reload = useCallback(async () => {
    try {
      const res = await fetch("/api/scanner/transactions");
      const data = await res.json();
      if (data.transactions) setTransactions(data.transactions);
      if (data.trades) setScannerTrades(data.trades);
      if (data.settlements) mergeSettlements(data.settlements);
    } catch {}
  }, []);

  return {
    transactions,
    wallets,
    scannerTrades,
    isPolling,
    lastPoll,
    errors,
    enabled,
    setEnabled,
    marketRate,
    lastTradeCreation,
    poll,
    confirmMatch,
    addWallet,
    deleteWallet,
    updateScannerTrade,
    reload,
  };
}
